<template>
    <ul
        id="questions"
        class="Questions">
        <li
            v-for="(question, index) in questions"
            :style="{
                paddingTop: `${ index === 0 ? (isMobile ? 120 : 260) : 100}px`,
                marginBottom: `${ index === questions.length - 1 ? (isMobile ? 'calc(100vh - 258px)' : 'calc(100vh - 312px)') : 0}`
            }"
            :key="index"
        >
            <hempel-question
                v-routing-animation="{speedModifier: 0}"
                :question="question.text"
                :answers="question.answers"
                :key="question.key"
                :type="question.key"
                :value="question.answer"
                :index="index + 1"
                :id="`question-${index}`"
                :big="!isMobile && index === 0"
                :total="questions.length"
                :active="index <= lastAnsweredQuestionIndex + 2"
                :visible="index === lastAnsweredQuestionIndex + 1"
                :dimensions="index === questions.length - 1"
                :ref="index"
                :antifouling="question.key === 'antifouling'"
                :help="question.key === 'antifouling'"
                :location="question.key === 'paintLocation'"
                :desktop="!isMobile"
                @change="onChange"
            />
        </li>
    </ul>
</template>
<script>
import HempelQuestion from '@/components/shared/question/Question'

const forms = {
    find: [
        'find/paintLocation',
        'find/waterType',
        'find/type',
        'find/material',
        'find/sailFrequency'
    ],
    calculate: [
        'find/paintLocation',
        'calculate/antifouling',
        'find/type',
        'calculate/keelType',
        'calculate/dimensions'
    ]
}

const waterTypeCountries = ['en-GB', 'da-DK', 'sv-SE_e', 'sv-SE_w', 'hr-HR']
const keelTypeBoats = ['sailing']
const hyphenateRE = /\B([A-Z])/g
const trackingKeyMap = {
    'paint-location': 'country',
    "type": 'boat-type'
}

export default {
    name: 'HempelFind',
    data() {
        return {
            loadedAnswers: {},
            questionsArray: [],
            scrollOptions: {
                duration: 1200,
                easing: [0.56, 0.05, 0.36, 1],
                cancelable: true,
                onStart: () => {
                    window.removeEventListener('scroll', this.calculateOffset, true)
                },
                onDone: () => {
                    window.addEventListener('scroll', this.calculateOffset, true)
                },
                onCancel: () => {
                    window.addEventListener('scroll', this.calculateOffset, true)
                },
                offset: () => (this.isMobile ? -120 : -280),
                x: false,
                y: true
            }
        }
    },
    computed: {
        form() {
            return forms[this.$route.name]
        },
        questions() {
            return this.form
                .map(type => {
                    const [form, field] = type.split('/')
                    const ids = this.getProductsForVolume.map(o => o.id)
                    const volumeProducts = this.getProductsForVolume.filter(
                        ({ id }, index) => !ids.includes(id, index + 1)
                    )
                    return {
                        key: field,
                        text: this.translation[form].questions[field],
                        answer: this.$route.query[field],
                        answers: this.translation[form].answers[field] || volumeProducts
                    }
                })
                .filter(question => {
                    if (
                        question.key === 'waterType' &&
                        (!this.$route.query.paintLocation ||
                            !waterTypeCountries.includes(this.$route.query.paintLocation))
                    ) {
                        return false
                    }
                    if (
                        question.key === 'keelType' &&
                        (!this.$route.query.type || !keelTypeBoats.includes(this.$route.query.type))
                    ) {
                        return false
                    }
                    return true
                })
        },
        lastAnsweredQuestion() {
            let index = -1
            for (let i = 0; i < this.questions.length; i++) {
                const answer = this.questions[i].answer
                if (answer || answer === 0) {
                    index = i
                } else if (index >= -1) {
                    return index
                }
            }
            return index
        },
        lastAnsweredQuestionIndex() {
            if (
                this.$route.name === 'calculate' &&
                this.$route.query.dimensions &&
                this.lastAnsweredQuestion === this.questions.length - 1
            ) {
                return this.lastAnsweredQuestion - 1
            }
            return this.lastAnsweredQuestion
        }
    },
    components: {
        HempelQuestion
    },
    watch: {
        $route: {
            handler(current, previous) {
                this.answersCheck()
                const firstTime = !previous || current.name !== previous.name
                const goingForwad =
                    firstTime ||
                    Object.keys(current.query).length >= Object.keys(previous.query).length
                if (goingForwad) {
                    this.trackSteps(firstTime, this.lastAnsweredQuestion)
                }
            },
            immediate: true
        }
    },
    methods: {
        hyphenate(str) {
            return str.replace(hyphenateRE, '-$1').toLowerCase()
        },
        trackSteps(initial = false, index = -1) {
            if (index < 0) {
                return
            }
            if (initial && index > 0) {
                this.trackSteps(initial, index - 1)
            }
            const rootPath = this.$route.name === 'find' ? 'find-product' : 'calculate-volume'
            const qNumber = index + 1
            let qKey = this.hyphenate(this.questions[index].key)
            qKey = trackingKeyMap[qKey] || qKey
            const qAnswer = this.questions[index].answer
            const path = `/${rootPath}/step-${qNumber}-${qKey}/${qAnswer}`
            this.dataLayerAdd(
                {
                    type: 'virtualPageview'
                },
                path
            )
        },
        scrollScreen(index) {
            setTimeout(() => {
                this.$scrollTo(`#question-${index + 1}`, this.scrollOptions)
            }, 200)
        },
        calculateOffset() {
            const offset = window.pageYOffset
            if (this.$route.path === '/find') {
                if (offset <= 100) this.updateQuestionState('0')
                if (offset > 100 && offset <= 350) this.updateQuestionState('1')
                if (offset > 350 && offset <= 650) this.updateQuestionState('2')
                if (offset > 650 && offset <= 850) this.updateQuestionState('3')
                if (offset > 850) this.updateQuestionState('4')
            } else {
                if (offset <= 100) this.updateQuestionState('0')
                if (offset > 100 && offset <= 550) this.updateQuestionState('1')
                if (offset > 550 && offset <= 850) this.updateQuestionState('2')
                if (offset > 850) this.updateQuestionState('3')
            }
        },
        updateQuestionState(index) {
            if (this.$route.path === '/find' || this.$route.path === '/calculate') {
                this.updateSingleQuestion(index)
                const item = this.questionsArray[index]
                if (!item) {
                    return
                }
                const initialQuestionState = item.className
                const questionElement = item.element
                if (
                    index <= this.lastAnsweredQuestionIndex &&
                    (initialQuestionState.indexOf('active') >= 0 &&
                        initialQuestionState.indexOf('visible') < 0)
                ) {
                    questionElement.className = `${initialQuestionState} visible`
                } else questionElement.className = initialQuestionState
            }
        },
        populateQuestionsArray() {
            if (this.$route.path === '/find' || this.$route.path === '/calculate') {
                this.questionsArray = []
                for (let i = 0; i < 5; i += 1) {
                    if (document.querySelector(`#question-${i}`)) {
                        this.questionsArray.push({
                            element: document.querySelector(`#question-${i}`),
                            className: document.querySelector(`#question-${i}`).className
                        })
                    }
                }
            }
        },
        updateSingleQuestion(index) {
            // Remove wood material if US or Canada
            if (
                this.$route.query.paintLocation === 'en-US' ||
                this.$route.query.paintLocation === 'en-CA'
            ) {
                const materialInputs = document.querySelectorAll('.Answer-label')
                const woodMaterial = 'Wood'
                // const woodMaterial = [
                //     'Wood',
                //     'Holz',
                //     'Træ',
                //     'Ξύλο',
                //     'Madera',
                //     'Puu',
                //     'Bois',
                //     'Drvo',
                //     'Tre',
                //     'Hout',
                //     'Trä'
                // ]
                let woodInput

                for (let i = 0; i < materialInputs.length; i++) {
                    if (materialInputs[i].textContent === woodMaterial) {
                        woodInput = materialInputs[i]
                        break
                    }
                }
                const woodWrapper = woodInput.closest('.Answer')
                woodWrapper.style.display = 'none'
            }

            if (this.$route.path === '/find' || this.$route.path === '/calculate') {
                const element = document.querySelector(`#question-${index}`)
                if (!element) {
                    return
                }
                this.$set(this.questionsArray, index, {
                    element,
                    className: element.className
                })
            }
        },
        handleAnalytics() {
            // let questionIndex = Object.keys(this.$route.query).length
            // if (this.$route.path === '/find') {
            //     if (this.$route.query.paintLocation !== 'en-GB'
            //         && this.$route.query.paintLocation !== 'da-DK'
            //         && questionIndex > 1) questionIndex += 1
            //     switch(true) {
            //         case (this.$route.query.hasOwnProperty('paintLocation') && questionIndex === 1):
            //             this.dataLayerAdd({ type: 'virtualPageview' },
            //                 `/find-product/step-${questionIndex}-country/${this.$route.query.paintLocation}`)
            //             break;
            //         case (this.$route.query.hasOwnProperty('waterType') && questionIndex === 2):
            //             this.dataLayerAdd({ type: 'virtualPageview' },
            //                 `/find-product/step-${questionIndex}-water-type/${this.$route.query.waterType}`)
            //             break;
            //         case (this.$route.query.hasOwnProperty('type') && questionIndex === 3):
            //             this.dataLayerAdd({ type: 'virtualPageview' },
            //                 `/find-product/step-${questionIndex}-boat-type/${this.$route.query.type}`)
            //             break;
            //         case (this.$route.query.hasOwnProperty('material') && questionIndex === 4):
            //             this.dataLayerAdd({ type: 'virtualPageview' },
            //                 `/find-product/step-${questionIndex}-material/${this.$route.query.material}`)
            //             break;
            //         case (this.$route.query.hasOwnProperty('sailFrequency') && questionIndex === 5):
            //             this.dataLayerAdd({ type: 'virtualPageview' },
            //                 `/find-product/step-${questionIndex}-sail-frequency/${this.$route.query.sailFrequency}`)
            //             break;
            //     }
            // } else {
            //     switch(true) {
            //         case (this.$route.query.hasOwnProperty('paintLocation') && questionIndex === 1):
            //             this.dataLayerAdd({ type: 'virtualPageview' },
            //                 `/calculate-volume/step-${questionIndex}-country/${this.$route.query.paintLocation}`)
            //             break;
            //         case (this.$route.query.hasOwnProperty('antifouling') && questionIndex === 2):
            //             this.dataLayerAdd({ type: 'virtualPageview' },
            //                 `/calculate-volume/step-${questionIndex}-antifouling/${this.$route.query.antifouling}`)
            //             break;
            //         case (this.$route.query.hasOwnProperty('type') && questionIndex === 3):
            //             this.dataLayerAdd({ type: 'virtualPageview' },
            //                 `/calculate-volume/step-${questionIndex}-boat-type/${this.$route.query.type}`)
            //             break;
            //         case (this.$route.query.hasOwnProperty('keelType') && questionIndex > 3 && questionIndex < 5):
            //             this.dataLayerAdd({ type: 'virtualPageview' },
            //                 `/calculate-volume/step-${questionIndex}-keel-type/${this.$route.query.keelType}`)
            //             break;
            //         case (this.$route.query.hasOwnProperty('dimensions') && questionIndex >= 4):
            //             this.dataLayerAdd({ type: 'virtualPageview' },
            //                 `/calculate-volume/step-${questionIndex}-dimensions/${this.$route.query.dimensions}`)
            //             break;
            //     }
            // }
        },
        answersCheck() {
            this.handleAnalytics()
            if (this.lastAnsweredQuestionIndex === this.questions.length - 1) {
                if (this.$route.name === 'find') {
                    setTimeout(
                        () =>
                            this.$router.replace({
                                path: `${this.$route.path}_results`,
                                query: this.$route.query
                            }),
                        1200
                    )
                }
                return
            }
            this.scrollScreen(this.lastAnsweredQuestionIndex)
        },
        onChange(e) {
            const method = e.replace ? 'replace' : 'push'
            const query = {}
            query[e.key] = e.value
            this.$router[method]({ query: Object.assign({}, this.$route.query, query) })
        },
        goCalculate() {
            this.getProductFromAPI()
            this.$router.replace({
                path: `${this.$route.path}_results`,
                query: this.$route.query
            })
        },
        checkOrigin() {
            if (Object.keys(this.$route.query).length > 0) {
                Object.keys(this.$route.query).forEach(key => this.handleAnalytics())
            }
        }
    },
    mounted() {
        if (window.history) {
            window.history.scrollRestoration = 'manual'
        }
        // this.checkOrigin()
    },
    destroyed() {
        if (window.history) {
            window.history.scrollRestoration = 'auto'
        }
    }
}
</script>
